/* ------------------------------------------------------------------ *
 * The reveal choreographer.
 *
 * ~90 lines of IntersectionObserver + MutationObserver instead of a
 * motion library. It does exactly two things to the DOM:
 *
 *   1. marks known containers `.pd-reveal` (+ `data-axis`) and flips
 *      them to `.is-in` when they enter the viewport;
 *   2. numbers each container's children in `--ri`.
 *
 * All actual motion lives in motion.css and keys off those hooks —
 * which is what keeps this file free of timing knowledge and the CSS
 * free of DOM knowledge.
 *
 * Because the MutationObserver watches the whole body, the same
 * choreography covers every arrival for free: route changes, query
 * data replacing hatch rows, palette results while typing, a manifest
 * opening in a Sheet. Nobody registers anything.
 *
 * With prefers-reduced-motion the module is a no-op: no classes, no
 * observers, no hidden states — the static app, untouched.
 * ------------------------------------------------------------------ */

interface GroupConfig {
  sel: string;
  /** 'x' — ledger rows slide from the leading edge; 'y' — cards rise. */
  axis: 'x' | 'y';
  /** Children that must stay still and visible (sticky heads). */
  skip?: string;
}

const GROUPS: GroupConfig[] = [
  { sel: '.pd-table__body', axis: 'x' },
  { sel: '.pd-board', axis: 'x', skip: '.pd-board__head' },
  { sel: '.pd-dash__metrics', axis: 'y' },
  { sel: '.pd-dash__list', axis: 'x' },
  { sel: '.pd-catalog', axis: 'y' },
  { sel: '.pd-buy__stand', axis: 'y' },
  { sel: '.pd-week__items', axis: 'y' },
  { sel: '.pd-inbox__threads', axis: 'x' },
  { sel: '.pd-convo__stream', axis: 'y' },
  { sel: '.pd-routes', axis: 'y' },
  { sel: '.pd-manifest-body__inner', axis: 'y' },
  { sel: '.pd-contact__body', axis: 'y' },
  { sel: '.pd-settings', axis: 'y' },
  { sel: '.pd-analytics', axis: 'y' },
  { sel: '.pd-palette__list', axis: 'x' },
];

/** Rows beyond the cap land together — a 60-row table must not take 3s. */
const CAP = 12;
/** A gap longer than this starts a new batch (data arriving later). */
const BATCH_RESET_MS = 160;
/** When the cascade has finished, indices reset so late remounts land NOW. */
const SETTLE_MS = 1400;

interface Meta {
  counter: number;
  last: number;
  cfg: GroupConfig;
}

export const initRevealChoreography = (): void => {
  if (typeof window === 'undefined') return;
  if (!('IntersectionObserver' in window)) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const meta = new WeakMap<Element, Meta>();
  const registered = new Set<Element>();

  /* The dead-man's switch.
   *
   * A group's children sit at opacity 0 until its observer fires. If
   * the observer NEVER fires — an extension broke it, the page booted
   * in a hidden tab and something went wrong on wake — that would be
   * an invisible app, which is unforgivable for a flourish. So: if no
   * intersection has been delivered within 3s of visible time, the
   * choreography declares itself dead, reveals everything at once and
   * stops hiding anything it registers from then on. Degraded mode is
   * the static app; broken mode is not allowed to exist. */
  let ioEverFired = false;
  let ioDead = false;

  const reveal = (group: Element): void => {
    io.unobserve(group);
    group.classList.add('is-in');
    window.setTimeout(() => {
      for (const child of group.children) {
        (child as HTMLElement).style.setProperty('--ri', '0');
      }
    }, SETTLE_MS);
  };

  const declareDead = (): void => {
    if (ioEverFired || ioDead) return;
    if (document.visibilityState !== 'visible') {
      document.addEventListener(
        'visibilitychange',
        () => window.setTimeout(declareDead, 1500),
        { once: true },
      );
      return;
    }
    ioDead = true;
    for (const group of registered) reveal(group);
  };

  window.setTimeout(declareDead, 3000);

  const io = new IntersectionObserver(
    (entries) => {
      ioEverFired = true;
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        reveal(entry.target);
      }
    },
    { threshold: 0.02 },
  );

  const indexChild = (child: Element, group: Element): void => {
    const m = meta.get(group);
    if (!m) return;
    const el = child as HTMLElement;
    if (m.cfg.skip && el.matches(m.cfg.skip)) {
      el.setAttribute('data-no-reveal', '');
      return;
    }
    const now = performance.now();
    if (now - m.last > BATCH_RESET_MS) m.counter = 0;
    m.last = now;
    el.style.setProperty('--ri', String(Math.min(m.counter++, CAP)));
  };

  const register = (group: Element, cfg: GroupConfig): void => {
    const el = group as HTMLElement;
    if (el.dataset['revealGroup']) return;
    el.dataset['revealGroup'] = cfg.axis;
    meta.set(group, { counter: 0, last: performance.now(), cfg });
    registered.add(group);
    el.classList.add('pd-reveal');
    el.setAttribute('data-axis', cfg.axis);
    for (const child of group.children) indexChild(child, group);
    if (ioDead) reveal(group);
    else io.observe(group);
  };

  const scan = (root: ParentNode): void => {
    for (const cfg of GROUPS) {
      if (root instanceof Element && root.matches(cfg.sel)) register(root, cfg);
      for (const found of root.querySelectorAll(cfg.sel)) register(found, cfg);
    }
  };

  scan(document.body);

  const mo = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        const parent = node.parentElement;
        if (parent && meta.has(parent)) indexChild(node, parent);
        scan(node);
      }
      for (const node of mutation.removedNodes) {
        if (!(node instanceof Element)) continue;
        // IntersectionObserver and the registry hold strong references;
        // without this, every route change would leak its containers.
        if (meta.has(node)) {
          io.unobserve(node);
          registered.delete(node);
        }
        for (const gone of node.querySelectorAll('[data-reveal-group]')) {
          io.unobserve(gone);
          registered.delete(gone);
        }
      }
    }
  });

  mo.observe(document.body, { childList: true, subtree: true });
};
